import { checkInactivity, warningPeriodMs } from './inactivity-timer.business';

const ACTIVITY_EVENTS = ['mousedown', 'touchstart', 'mousemove', 'keydown', 'scroll'];

export interface InactivityTimer {
  restart: VoidFunction;
  stop: VoidFunction;
}

interface InactivityTimerOptions {
  timeoutMs: number;
  warningMs: number;
  onWarning: (deadline: number) => void;
  onTimeout: VoidFunction;
}

export const startInactivityTimer = (options: InactivityTimerOptions): InactivityTimer => {
  const { timeoutMs, onWarning, onTimeout } = options;
  const warningMs = warningPeriodMs(options.warningMs, timeoutMs);
  let deadline = 0;
  let isWarning = false;
  let isStopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const check = () => {
    clearTimeout(timer);
    const result = checkInactivity({ deadline, now: Date.now(), warningMs, isWarning });
    if (result.kind === 'timeout') {
      stop();
      onTimeout();
      return;
    }
    if (result.isWarning && !isWarning) {
      isWarning = true;
      onWarning(deadline);
    }
    timer = setTimeout(check, result.nextCheckInMs);
  };

  const restart = () => {
    if (isStopped) {
      return;
    }
    isWarning = false;
    deadline = Date.now() + timeoutMs;
    check();
  };

  const restartUnlessWarning = () => {
    if (!isWarning) {
      restart();
    }
  };

  // The Android web view freezes timers while the app is in the background.
  const recheckDeadlineOnResume = () => {
    if (document.visibilityState === 'visible') {
      check();
    }
  };

  function stop() {
    isStopped = true;
    clearTimeout(timer);
    ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, restartUnlessWarning, false));
    document.removeEventListener('visibilitychange', recheckDeadlineOnResume);
  }

  ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, restartUnlessWarning, false));
  document.addEventListener('visibilitychange', recheckDeadlineOnResume);
  restart();

  return { restart, stop };
};
